import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { CheckBoxComp, DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, ListDTO, ListFilterDTO, UserDTO, ValueListDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';

interface IValueListMaintenanceProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const ValueListMaintenance: React.FC<IValueListMaintenanceProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [list, setList] = useState<ListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [valueListDTO, setValueListDTO] = useState<ValueListDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setValueListDTO({
            ...valueListDTO,
            id: '',
            value: '',
            listId: parentId ?? data.id,
            valueIndex: valueListDTO?.valueIndex ? valueListDTO?.valueIndex + 1 : 0,
            description: '',
            isSystem: false,
            companyId: userInfo.user.companyId,
        } as ValueListDTO);

        if(isOpenMaintenance){
            handleListGetAllAsync();
            handleUserGetAll();
            handleGetByIdAsync();

            handleGetPermission(ListId.parmsphere.ValueListId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList,);
    }, []);
    
    const handleUserGetAll = useCallback(async () => {
        const filterUser = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', filterUser, setLoading, setUserList, showMessage);
    }, []);

    const handleListGetAllAsync = useCallback(async () => {
        const listFilterDTO: ListFilterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllViewAsync1('parmsphere.list', listFilterDTO, setActionExecuted, setLoading, setList, showMessage);
    }, []);

    const handleGetByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('parmsphere.valuelist', data.id, setLoading, setValueListDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        if(valueListDTO?.isSystem){
            showMessage('There is no permission to change system parameters.', 'info');
            return;
        }

        const dto = valueListDTO as ValueListDTO;
        if (valueListDTO?.id === '' || valueListDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('parmsphere.valuelist', dto, setValueListDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if(!accessRolePermissionDTO?.allowUpdate) {
                showMessage('There is no permission for this access.', 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('parmsphere.valuelist', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [valueListDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);

        if (type != 'success')
            setIsOpenMaintenance(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'close', text: 'Close', icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='VALUE DETAILS'
                icon='add_task_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <TextFieldInput
                                id='value'
                                label='Value'
                                value={valueListDTO?.value}
                                setValue={setValueListDTO}
                                obj={valueListDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='valueIndex'
                                label='Index'
                                value={valueListDTO?.valueIndex}
                                setValue={setValueListDTO}
                                obj={valueListDTO}
                                required={true}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <CheckBoxComp
                                id='isSystem'
                                label='Is System'
                                setValue={setValueListDTO}
                                objDTO={valueListDTO}
                                defaultChecked={valueListDTO?.isSystem}
                                disabled={valueListDTO?.isSystem}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={valueListDTO?.description}
                                setValue={setValueListDTO}
                                obj={valueListDTO}
                                required={true}
                                multiline={true}
                                rows={4}
                            />
                        </Grid>
                        {valueListDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={valueListDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === valueListDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(valueListDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === valueListDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(valueListDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};